import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Head, router, useForm, usePage } from "@inertiajs/react";
import { Dropdown } from "bootstrap";
import Tooltip from "bootstrap/js/dist/tooltip";
import AuthLayout from "@/layouts/auth-layout";
import {
  edit_organization_file_path,
  organization_file_folder_path,
  organization_file_folders_path,
  organization_file_path,
  organization_files_path,
  move_organization_files_path,
  sync_organization_file_path
} from "@/routes";
import { useConfirmDialog } from "@/components/confirm-dialog-provider";

function filenameKey(filename = "") {
  return filename.trim().toLocaleLowerCase("da-DK");
}

function compareText(first = "", second = "") {
  return first.localeCompare(second, "da-DK", { sensitivity: "base", numeric: true });
}

function formatDate(value) {
  if (!value) return "";

  return new Intl.DateTimeFormat("da-DK", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function formatBytes(value = 0) {
  if (!value) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const size = value / (1024 ** index);

  return `${new Intl.NumberFormat("da-DK", { maximumFractionDigits: index === 0 ? 0 : 1 }).format(size)} ${units[index]}`;
}

function fileTypeLabel(file) {
  const filenameExtension = file.filename?.split(".").pop()?.toUpperCase();
  if (filenameExtension && filenameExtension !== file.filename?.toUpperCase()) return filenameExtension;
  if (file.content_type?.startsWith("image/")) return "Billede";
  if (file.content_type?.startsWith("text/")) return "Tekst";

  return "Fil";
}

function onlyofficeEditIcon(file) {
  return file.filename?.toLocaleLowerCase("da-DK").endsWith(".docx")
    ? "bi-file-earmark-word"
    : "bi-grid-3x3-gap";
}

function SortHeader({ label, sortKey, sort, onSort, className = "" }) {
  const isActive = sort.key === sortKey;
  const nextDirection = isActive && sort.direction === "asc" ? "desc" : "asc";
  const ariaSort = isActive ? (sort.direction === "asc" ? "ascending" : "descending") : "none";

  return (
    <th className={className} aria-sort={ariaSort}>
      <button
        type="button"
        className="organization-files-table__sort"
        onClick={() => onSort(sortKey)}
        aria-label={`Sorter efter ${label.toLocaleLowerCase("da-DK")} ${nextDirection === "asc" ? "stigende" : "faldende"}`}
      >
        <span>{label}</span>
        <i className={`bi ${isActive && sort.direction === "desc" ? "bi-arrow-down" : "bi-arrow-up"}`} aria-hidden="true" />
      </button>
    </th>
  );
}

function OpenaiStatusIcon({ file }) {
  if (file.openai_sync_error) {
    const errorMessage = `AI-synkronisering fejlede: ${file.openai_sync_error}`;

    return (
      <i
        className="bi bi-exclamation-triangle-fill text-danger fs-5"
        data-bs-toggle="tooltip"
        data-bs-placement="left"
        title={errorMessage}
        aria-label={errorMessage}
      />
    );
  }

  if (file.openai_synced_at) {
    return (
      <i
        className="bi bi-check-circle-fill text-success fs-5"
        data-bs-toggle="tooltip"
        data-bs-placement="left"
        title="Synkroniseret til AI."
        aria-label="Synkroniseret til AI."
      />
    );
  }

  return (
    <span
      className="spinner-border spinner-border-sm text-secondary"
      data-bs-toggle="tooltip"
      data-bs-placement="left"
      role="status"
      title="Synkronisering igang."
      aria-label="Synkronisering igang."
    />
  );
}

function RowActionMenu({ label, children }) {
  const toggleRef = useRef(null);

  useEffect(() => {
    const dropdown = new Dropdown(toggleRef.current, {
      boundary: "viewport",
      popperConfig: (config) => ({ ...config, strategy: "fixed" })
    });

    return () => dropdown.dispose();
  }, []);

  return (
    <div
      className="dropdown organization-files-table__menu"
      onDragStart={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <button
        ref={toggleRef}
        type="button"
        className="btn btn-outline-secondary organization-files-table__menu-toggle"
        data-bs-toggle="dropdown"
        data-bs-boundary="viewport"
        aria-expanded="false"
        aria-label={label}
        title="Handlinger"
      >
        <i className="bi bi-three-dots-vertical" aria-hidden="true" />
      </button>
      <ul className="dropdown-menu dropdown-menu-end organization-files-table__menu-list">
        {children}
      </ul>
    </div>
  );
}

function MoveFilesDialog({ isOpen, count, folders, currentFolderId, targetFolderId, moving, onTargetChange, onCancel, onMove }) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !moving) onCancel();
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, moving, onCancel]);

  if (!isOpen || typeof document === "undefined") return null;

  const currentValue = currentFolderId == null ? "" : String(currentFolderId);
  const targetIsCurrentFolder = targetFolderId === currentValue;
  const sortedFolders = [...folders].sort((first, second) => compareText(first.path, second.path));

  return createPortal(
    <div className="organization-files-move-dialog" role="presentation">
      <button type="button" className="organization-files-move-dialog__backdrop" aria-label="Luk flytning" onClick={moving ? undefined : onCancel} />
      <section className="organization-files-move-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="organization-files-move-dialog-title">
        <div>
          <div className="workspace-section__eyebrow">Flyt filer</div>
          <h2 className="organization-files-move-dialog__title" id="organization-files-move-dialog-title">
            Vælg destinationsmappe
          </h2>
          <p className="organization-files-move-dialog__description">
            {count === 1 ? "Den valgte fil flyttes til mappen." : `${count} valgte filer flyttes samlet til mappen.`}
          </p>
        </div>

        <label className="form-label fw-semibold" htmlFor="organization-files-move-target">Mappe</label>
        <select
          id="organization-files-move-target"
          className="form-select"
          value={targetFolderId}
          autoFocus
          disabled={moving}
          onChange={(event) => onTargetChange(event.target.value)}
        >
          <option value="/">Vælg en mappe…</option>
          <option value="">/ (Alle filer)</option>
          {sortedFolders.map((folder) => (
            <option key={folder.id} value={String(folder.id)}>/{folder.path}</option>
          ))}
        </select>

        <div className="organization-files-move-dialog__actions">
          <button type="button" className="btn btn-outline-secondary" disabled={moving} onClick={onCancel}>Annuller</button>
          <button type="button" className="btn btn-primary" disabled={moving || targetFolderId === "/" || targetIsCurrentFolder} onClick={onMove}>
            {moving ? "Flytter…" : "Flyt"}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}

function OrganizationFilesIndex({
  organization_files: organizationFiles = [],
  organization_file_folders: folders = [],
  current_folder_id: currentFolderId = null,
  max_folder_depth: maxFolderDepth = 10
}) {
  const { current_tenant: currentTenant, max_file_size: maxFileSize } = usePage().props;
  const { confirm } = useConfirmDialog();
  const filesTableRef = useRef(null);
  const fileDragInProgressRef = useRef(false);
  const selectionAnchorIdRef = useRef(null);
  const [inputKey, setInputKey] = useState(0);
  const [fileSizeError, setFileSizeError] = useState("");
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingFilename, setEditingFilename] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [renameError, setRenameError] = useState("");
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragTargetId, setDragTargetId] = useState(null);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState("/");
  const [movingFiles, setMovingFiles] = useState(false);
  const [sort, setSort] = useState({ key: "filename", direction: "asc" });
  const { data, setData, post, processing, reset } = useForm({
    organization_file: {
      files: [],
      folder_id: currentFolderId,
      conflict_resolution: "keep_both"
    }
  });

  const foldersById = useMemo(() => new Map(folders.map((folder) => [folder.id, folder])), [folders]);
  const foldersByParent = useMemo(() => {
    const grouped = new Map();

    folders.forEach((folder) => {
      const parentId = folder.parent_id || null;
      const siblings = grouped.get(parentId) || [];
      siblings.push(folder);
      grouped.set(parentId, siblings);
    });

    grouped.forEach((siblings) => siblings.sort((first, second) => compareText(first.name, second.name)));
    return grouped;
  }, [folders]);
  const currentFolder = currentFolderId ? foldersById.get(currentFolderId) : null;
  const parentFolderId = currentFolder?.parent_id || null;
  const currentDepth = currentFolder?.depth || 0;
  const canCreateFolder = currentDepth < maxFolderDepth;
  const visibleFolders = foldersByParent.get(currentFolderId || null) || [];
  const visibleFiles = useMemo(
    () => organizationFiles.filter((file) => (file.folder_id || null) === (currentFolderId || null)),
    [currentFolderId, organizationFiles]
  );
  const selectedFiles = data.organization_file.files || [];
  const hasPendingFiles = organizationFiles.some((file) => !file.openai_sync_error && !file.openai_synced_at);
  const uploadConflicts = useMemo(() => {
    const filesByName = new Map(visibleFiles.map((file) => [filenameKey(file.filename), file]));
    const seenNames = new Set();

    return selectedFiles.filter((file) => {
      const key = filenameKey(file.name || "");
      if (!filesByName.has(key) || seenNames.has(key)) return false;

      seenNames.add(key);
      return true;
    }).map((file) => filesByName.get(filenameKey(file.name || "")));
  }, [selectedFiles, visibleFiles]);
  const hasUploadConflicts = uploadConflicts.length > 0;
  const canReplaceUploadConflicts = hasUploadConflicts && uploadConflicts.every((file) => file.can_destroy);
  const sortedVisibleFiles = useMemo(() => {
    const direction = sort.direction === "asc" ? 1 : -1;

    return [...visibleFiles].sort((first, second) => {
      if (sort.key === "byte_size") return (first.byte_size - second.byte_size) * direction;
      if (sort.key === "created_at") return (new Date(first.created_at) - new Date(second.created_at)) * direction;
      if (sort.key === "file_type") {
        const typeComparison = compareText(fileTypeLabel(first), fileTypeLabel(second));
        if (typeComparison !== 0) return typeComparison * direction;
      }

      return compareText(first.filename, second.filename) * direction;
    });
  }, [sort, visibleFiles]);
  const selectedFileIdSet = useMemo(() => new Set(selectedFileIds), [selectedFileIds]);
  const breadcrumbs = useMemo(() => {
    const items = [];
    const seenIds = new Set();
    let folder = currentFolder;

    while (folder && !seenIds.has(folder.id)) {
      items.unshift(folder);
      seenIds.add(folder.id);
      folder = folder.parent_id ? foldersById.get(folder.parent_id) : null;
    }

    return items;
  }, [currentFolder, foldersById]);

  useEffect(() => {
    setData("organization_file", {
      ...data.organization_file,
      folder_id: currentFolderId
    });
    setSelectedFileIds([]);
    selectionAnchorIdRef.current = null;
    setMoveDialogOpen(false);
    setMoveTargetFolderId("/");
  }, [currentFolderId]);

  useEffect(() => {
    const visibleFileIdSet = new Set(visibleFiles.map((file) => file.id));
    setSelectedFileIds((currentIds) => currentIds.filter((fileId) => visibleFileIdSet.has(fileId)));
    if (selectionAnchorIdRef.current && !visibleFileIdSet.has(selectionAnchorIdRef.current)) {
      selectionAnchorIdRef.current = null;
    }
  }, [visibleFiles]);

  useEffect(() => {
    if (data.organization_file.conflict_resolution !== "replace" || canReplaceUploadConflicts) return;

    setData("organization_file", {
      ...data.organization_file,
      conflict_resolution: "keep_both"
    });
  }, [canReplaceUploadConflicts]);

  useEffect(() => {
    if (!hasPendingFiles) return undefined;

    const timer = window.setInterval(() => {
      router.reload({ only: ["organization_files"], preserveScroll: true });
    }, 3000);

    return () => window.clearInterval(timer);
  }, [hasPendingFiles]);

  useEffect(() => {
    if (!filesTableRef.current) return undefined;

    const tooltips = Array.from(filesTableRef.current.querySelectorAll('[data-bs-toggle="tooltip"]')).map((element) => new Tooltip(element));

    return () => tooltips.forEach((tooltip) => tooltip.dispose());
  }, [organizationFiles]);

  const navigateToFolder = (folderId) => {
    router.get(organization_files_path(), folderId ? { folder_id: folderId } : {}, {
      preserveScroll: true,
      preserveState: true
    });
  };

  const submitUpload = (event) => {
    event.preventDefault();
    if (selectedFiles.length === 0 || fileSizeError) return;

    post(organization_files_path(), {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        reset("organization_file");
        setData("organization_file", {
          files: [],
          folder_id: currentFolderId,
          conflict_resolution: "keep_both"
        });
        setFileSizeError("");
        setInputKey((key) => key + 1);
      }
    });
  };

  const selectFiles = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    const oversizedFiles = nextFiles.filter((file) => file.size > maxFileSize);

    if (oversizedFiles.length > 0) {
      setData("organization_file", { files: [], folder_id: currentFolderId, conflict_resolution: "keep_both" });
      setFileSizeError(`Filer må højst være ${formatBytes(maxFileSize)}.`);
      setInputKey((key) => key + 1);
      return;
    }

    setFileSizeError("");
    setData("organization_file", {
      files: nextFiles,
      folder_id: currentFolderId,
      conflict_resolution: "keep_both"
    });
  };

  const updateUploadConflictResolution = (conflictResolution) => {
    setData("organization_file", { ...data.organization_file, conflict_resolution: conflictResolution });
  };

  const updateSort = (key) => {
    setSort((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === "asc" ? "desc" : "asc"
    }));
  };

  const selectFile = (file, event) => {
    const additiveSelection = event.metaKey || event.ctrlKey;
    const selectableFileIds = sortedVisibleFiles.filter((visibleFile) => visibleFile.can_update).map((visibleFile) => visibleFile.id);
    const anchorIndex = selectableFileIds.indexOf(selectionAnchorIdRef.current);
    const fileIndex = selectableFileIds.indexOf(file.id);

    if (event.shiftKey && anchorIndex >= 0 && fileIndex >= 0) {
      const rangeStart = Math.min(anchorIndex, fileIndex);
      const rangeEnd = Math.max(anchorIndex, fileIndex);
      const rangeIds = selectableFileIds.slice(rangeStart, rangeEnd + 1);

      setSelectedFileIds((currentIds) => (
        additiveSelection ? [...new Set([...currentIds, ...rangeIds])] : rangeIds
      ));
      return;
    }

    selectionAnchorIdRef.current = file.id;
    if (additiveSelection) {
      setSelectedFileIds((currentIds) => (
        currentIds.includes(file.id)
          ? currentIds.filter((fileId) => fileId !== file.id)
          : [...currentIds, file.id]
      ));
      return;
    }

    setSelectedFileIds([file.id]);
  };

  const toggleFileSelectionFromRow = (event, file) => {
    if (fileDragInProgressRef.current) return;
    if (!file.can_update || editingFileId === file.id) return;
    if (event.target.closest("a, button, input, select, textarea, form, [role='menu']")) return;

    selectFile(file, event);
  };

  const handleFileRowKeyDown = (event, file) => {
    if (event.target !== event.currentTarget || !file.can_update || editingFileId === file.id) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    selectFile(file, event);
  };

  const clearFileSelection = () => {
    selectionAnchorIdRef.current = null;
    setSelectedFileIds([]);
  };

  const openMoveDialog = () => {
    setMoveTargetFolderId("/");
    setMoveDialogOpen(true);
  };

  const moveFiles = (fileIds, targetFolderId, options = {}) => {
    if (fileIds.length === 0) return;

    setMovingFiles(true);
    router.patch(move_organization_files_path(), {
      file_ids: fileIds,
      folder_id: targetFolderId,
      current_folder_id: currentFolderId
    }, {
      preserveScroll: true,
      onFinish: () => {
        setMovingFiles(false);
        options.onFinish?.();
      }
    });
  };

  const submitMoveDialog = () => {
    if (moveTargetFolderId === "/") return;

    moveFiles(selectedFileIds, moveTargetFolderId || null, {
      onFinish: () => setMoveDialogOpen(false)
    });
  };

  const submitFolder = (event) => {
    event.preventDefault();
    const name = newFolderName.trim();
    if (!name || !canCreateFolder) return;

    setCreatingFolder(true);
    router.post(organization_file_folders_path(), {
      organization_file_folder: { name, parent_id: currentFolderId }
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setNewFolderName("");
        setShowFolderForm(false);
      },
      onFinish: () => setCreatingFolder(false)
    });
  };

  const cancelCreatingFolder = () => {
    setNewFolderName("");
    setShowFolderForm(false);
  };

  const destroyFile = async (file) => {
    const confirmed = await confirm({
      title: "Fjern fil",
      description: `Vil du fjerne ${file.filename} fra ${currentTenant?.name}?`,
      confirmLabel: "Fjern",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(organization_file_path(file.id), {
      data: { current_folder_id: currentFolderId },
      preserveScroll: true
    });
  };

  const retryOpenaiSync = (file) => {
    router.post(sync_organization_file_path(file.id), {
      current_folder_id: currentFolderId
    }, { preserveScroll: true });
  };

  const destroyFolder = async (folder) => {
    const confirmed = await confirm({
      title: "Slet mappe",
      description: `Vil du slette mappen ${folder.name}? Mappen skal være tom.`,
      confirmLabel: "Slet",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(organization_file_folder_path(folder.id), {
      data: { current_folder_id: currentFolderId },
      preserveScroll: true
    });
  };

  const startRenamingFile = (file) => {
    setEditingFolderId(null);
    setEditingFileId(file.id);
    setEditingFilename(file.filename);
    setRenameError("");
  };

  const startRenamingFolder = (folder) => {
    setEditingFileId(null);
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
    setRenameError("");
  };

  const cancelRenaming = () => {
    setEditingFileId(null);
    setEditingFilename("");
    setEditingFolderId(null);
    setEditingFolderName("");
    setRenameError("");
  };

  const submitFileRename = (event, file) => {
    event.preventDefault();
    const filename = editingFilename.trim();

    if (!filename) {
      setRenameError("Filnavn må ikke være tomt.");
      return;
    }

    router.patch(organization_file_path(file.id), {
      organization_file: { filename },
      current_folder_id: currentFolderId
    }, { preserveScroll: true, onSuccess: cancelRenaming });
  };

  const submitFolderRename = (event, folder) => {
    event.preventDefault();
    const name = editingFolderName.trim();

    if (!name) {
      setRenameError("Mappenavn må ikke være tomt.");
      return;
    }

    router.patch(organization_file_folder_path(folder.id), {
      organization_file_folder: { name },
      current_folder_id: currentFolderId
    }, { preserveScroll: true, onSuccess: cancelRenaming });
  };

  const beginDrag = (event, item) => {
    event.stopPropagation();
    fileDragInProgressRef.current = true;
    let dragItem = item;

    if (item.type === "file") {
      const fileIds = selectedFileIdSet.has(item.id) ? selectedFileIds : [item.id];
      dragItem = { type: "files", ids: fileIds, folderId: item.folderId };
      if (!selectedFileIdSet.has(item.id)) {
        selectionAnchorIdRef.current = item.id;
        setSelectedFileIds([item.id]);
      }
      setFileDragImage(event, fileIds);
    }

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/json", JSON.stringify(dragItem));
    event.dataTransfer.setData("text/plain", dragItem.type === "files" ? `${dragItem.ids.length} file(s)` : "folder");
    setDraggedItem(dragItem);
  };

  const setFileDragImage = (event, fileIds) => {
    if (typeof document === "undefined" || typeof event.dataTransfer.setDragImage !== "function") return;

    const filesById = new Map(organizationFiles.map((file) => [file.id, file]));
    const filenames = fileIds.map((fileId) => filesById.get(fileId)?.filename).filter(Boolean);
    const preview = document.createElement("div");
    preview.className = `organization-files-drag-preview${fileIds.length > 1 ? " is-multiple" : ""}`;

    if (fileIds.length > 1) {
      const backLayer = document.createElement("div");
      backLayer.className = "organization-files-drag-preview__layer organization-files-drag-preview__layer--back";
      preview.appendChild(backLayer);

      const middleLayer = document.createElement("div");
      middleLayer.className = "organization-files-drag-preview__layer organization-files-drag-preview__layer--middle";
      preview.appendChild(middleLayer);
    }

    const card = document.createElement("div");
    card.className = "organization-files-drag-preview__card";

    const icon = document.createElement("i");
    icon.className = fileIds.length > 1 ? "bi bi-files" : "bi bi-file-earmark";
    icon.setAttribute("aria-hidden", "true");
    card.appendChild(icon);

    const text = document.createElement("div");
    text.className = "organization-files-drag-preview__text";

    const title = document.createElement("strong");
    title.textContent = fileIds.length === 1 ? (filenames[0] || "1 fil") : `${fileIds.length} filer`;
    text.appendChild(title);

    if (fileIds.length > 1) {
      const details = document.createElement("span");
      const visibleNames = filenames.slice(0, 3);
      const remainingCount = fileIds.length - visibleNames.length;
      details.textContent = `${visibleNames.join(" · ")}${remainingCount > 0 ? ` · +${remainingCount}` : ""}`;
      text.appendChild(details);
    }

    card.appendChild(text);
    preview.appendChild(card);
    document.body.appendChild(preview);
    event.dataTransfer.setDragImage(preview, 28, 24);
    window.requestAnimationFrame(() => preview.remove());
  };

  const clearDragState = () => {
    setDraggedItem(null);
    setDragTargetId(null);
    window.setTimeout(() => {
      fileDragInProgressRef.current = false;
    }, 0);
  };

  const folderContainsTarget = (sourceFolderId, targetFolderId) => {
    let folderId = targetFolderId;
    const seenIds = new Set();

    while (folderId && !seenIds.has(folderId)) {
      if (folderId === sourceFolderId) return true;
      seenIds.add(folderId);
      folderId = foldersById.get(folderId)?.parent_id;
    }

    return false;
  };

  const canDrop = (targetFolderId) => {
    if (!draggedItem) return false;
    if (draggedItem.type === "file" || draggedItem.type === "files") return (draggedItem.folderId || null) !== (targetFolderId || null);
    if (draggedItem.type === "folder") {
      if ((draggedItem.parentId || null) === (targetFolderId || null)) return false;
      return !folderContainsTarget(draggedItem.id, targetFolderId);
    }

    return false;
  };

  const handleDragOver = (event, targetFolderId, dropKey) => {
    if (!canDrop(targetFolderId)) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    setDragTargetId(dropKey);
  };

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setDragTargetId(null);
  };

  const handleDrop = (event, targetFolderId) => {
    event.preventDefault();
    event.stopPropagation();

    let item = draggedItem;
    if (!item) {
      try {
        item = JSON.parse(event.dataTransfer.getData("application/json"));
      } catch (_error) {
        clearDragState();
        return;
      }
    }

    if (!item || (item.type === "folder" && folderContainsTarget(item.id, targetFolderId))) {
      clearDragState();
      return;
    }

    if (item.type === "file" || item.type === "files") {
      const fileIds = item.type === "files" ? item.ids : [item.id];
      moveFiles(fileIds, targetFolderId, { onFinish: clearDragState });
      return;
    }

    router.patch(organization_file_folder_path(item.id), {
      organization_file_folder: { parent_id: targetFolderId },
      current_folder_id: currentFolderId
    }, { preserveScroll: true, onFinish: clearDragState });
  };

  const renderFolderRename = (folder) => (
    <form className="organization-files-table__rename" onSubmit={(event) => submitFolderRename(event, folder)}>
      <input
        type="text"
        className="form-control form-control-sm"
        value={editingFolderName}
        maxLength="100"
        autoFocus
        onChange={(event) => {
          setEditingFolderName(event.target.value);
          setRenameError("");
        }}
      />
      <button type="submit" className="btn btn-sm btn-primary">Gem</button>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelRenaming}>Annuller</button>
      {renameError ? <div className="organization-files-table__rename-error">{renameError}</div> : null}
    </form>
  );

  return (
    <>
      <Head title="Filer" />

      <section className="workspace-content organization-files-page">
        <section className="card">
          <div className="card-body">
            <div className="organization-files-header">
              <div>
                <div className="workspace-section__eyebrow">Organisation</div>
                <h2 className="workspace-section__title">Filer i {currentTenant?.name}</h2>
              </div>

              <div className="organization-files-header__actions">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={!canCreateFolder}
                  title={canCreateFolder ? "Opret en undermappe her" : `Maksimal mappedybde er ${maxFolderDepth}`}
                  onClick={() => {
                    if (showFolderForm) setNewFolderName("");
                    setShowFolderForm((visible) => !visible);
                  }}
                >
                  <i className="bi bi-folder-plus me-2" aria-hidden="true" />
                  Ny mappe
                </button>
              </div>
            </div>

            <form className="organization-files-upload mt-4" onSubmit={submitUpload}>
              <div>
                <div className="fw-semibold">Upload til denne mappe</div>
                <div className="text-muted small">Maks. {formatBytes(maxFileSize)} pr. fil</div>
              </div>

              <div className="organization-files-upload__control">
                <input
                  key={inputKey}
                  type="file"
                  multiple
                  className="form-control"
                  onChange={(event) => selectFiles(event.target.files)}
                />
                <button type="submit" className="btn btn-primary" disabled={processing || selectedFiles.length === 0 || Boolean(fileSizeError)}>
                  <i className="bi bi-upload me-2" aria-hidden="true" />
                  <span>{processing ? "Uploader" : `Upload${selectedFiles.length > 1 ? ` ${selectedFiles.length}` : ""}`}</span>
                </button>
                {fileSizeError ? <div className="organization-files-upload__error">{fileSizeError}</div> : null}
                {hasUploadConflicts ? (
                  <div className="organization-files-upload__conflicts">
                    <div className="organization-files-upload__conflict-title">
                      {uploadConflicts.length === 1 ? "Der findes allerede en fil med samme navn i mappen." : `${uploadConflicts.length} filer findes allerede med samme navn i mappen.`}
                    </div>
                    <div className="organization-files-upload__conflict-options" role="radiogroup" aria-label="Vælg håndtering af filer med samme navn">
                      <input type="radio" className="btn-check" name="organization-file-conflict-resolution" id="organization-file-conflict-keep-both" checked={data.organization_file.conflict_resolution !== "replace"} onChange={() => updateUploadConflictResolution("keep_both")} />
                      <label className="btn btn-sm btn-outline-secondary" htmlFor="organization-file-conflict-keep-both">Behold begge</label>
                      <input type="radio" className="btn-check" name="organization-file-conflict-resolution" id="organization-file-conflict-replace" checked={data.organization_file.conflict_resolution === "replace"} disabled={!canReplaceUploadConflicts} onChange={() => updateUploadConflictResolution("replace")} />
                      <label className={`btn btn-sm btn-outline-secondary${canReplaceUploadConflicts ? "" : " disabled"}`} htmlFor="organization-file-conflict-replace">Erstat eksisterende</label>
                    </div>
                    {!canReplaceUploadConflicts ? <div className="organization-files-upload__conflict-note">Du kan kun erstatte filer, du selv må fjerne.</div> : null}
                  </div>
                ) : null}
              </div>
            </form>

            <nav className="organization-files-breadcrumbs" aria-label="Mappesti">
              <button
                type="button"
                className="organization-files-breadcrumbs__item"
                onClick={() => navigateToFolder(null)}
              >
                <i className="bi bi-house-door" aria-hidden="true" />
                <span>Start</span>
              </button>
              {breadcrumbs.map((folder) => (
                <span className="organization-files-breadcrumbs__segment" key={folder.id}>
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                  <button
                    type="button"
                    className="organization-files-breadcrumbs__item"
                    onClick={() => navigateToFolder(folder.id)}
                  >
                    {folder.name}
                  </button>
                </span>
              ))}
            </nav>

            {selectedFileIds.length > 0 ? (
              <div className="organization-files-selection-bar" role="region" aria-label="Handlinger for valgte filer">
                <div className="organization-files-selection-bar__count">
                  <i className="bi bi-check2-square" aria-hidden="true" />
                  <span>{selectedFileIds.length === 1 ? "1 fil valgt" : `${selectedFileIds.length} filer valgt`}</span>
                </div>
                <div className="organization-files-selection-bar__actions">
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={movingFiles} onClick={clearFileSelection}>Ryd valg</button>
                  <button type="button" className="btn btn-sm btn-primary" disabled={movingFiles} onClick={openMoveDialog}>
                    <i className="bi bi-folder-symlink me-2" aria-hidden="true" />
                    Flyt…
                  </button>
                </div>
              </div>
            ) : null}

            <div className="organization-files-browser mt-3">
              <div className="workspace-admin-table organization-files-list" ref={filesTableRef}>
                <div className="table-responsive">
                  <table className="table align-middle mb-0 organization-files-table">
                    <colgroup>
                      <col className="organization-files-table__name-column" />
                      <col className="organization-files-table__type-column" />
                      <col className="organization-files-table__size-column" />
                      <col className="organization-files-table__upload-column" />
                      <col className="organization-files-table__upload-column" />
                      <col className="organization-files-table__status-column" />
                      <col className="organization-files-table__actions-column" />
                    </colgroup>
                    <thead>
                      <tr>
                        <SortHeader label="Navn" sortKey="filename" sort={sort} onSort={updateSort} />
                        <SortHeader label="Type" sortKey="file_type" sort={sort} onSort={updateSort} />
                        <SortHeader label="Størrelse" sortKey="byte_size" sort={sort} onSort={updateSort} />
                        <SortHeader label="Uploadet" sortKey="created_at" sort={sort} onSort={updateSort} />
                        <th>Sidst redigeret af</th>
                        <th>AI-status</th>
                        <th className="text-end">Handling</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentFolder ? (
                        <tr
                          className={`organization-files-table__parent-row${dragTargetId === "parent-row" ? " is-drop-target" : ""}`}
                          onDragOver={(event) => handleDragOver(event, parentFolderId, "parent-row")}
                          onDragLeave={handleDragLeave}
                          onDrop={(event) => handleDrop(event, parentFolderId)}
                        >
                          <td colSpan="7">
                            <button type="button" className="organization-files-table__file organization-files-table__parent-link" aria-label="Gå til overordnet mappe" title="Gå til overordnet mappe" onClick={() => navigateToFolder(parentFolderId)}>
                              <span className="organization-files-table__icon organization-files-table__icon--parent"><i className="bi bi-arrow-90deg-up" aria-hidden="true" /></span>
                              <strong>..</strong>
                            </button>
                          </td>
                        </tr>
                      ) : null}

                      {showFolderForm ? (
                        <tr className="organization-files-table__new-folder-row">
                          <td>
                            <div className="organization-files-table__file">
                              <span className="organization-files-table__icon organization-files-table__icon--folder"><i className="bi bi-folder-fill" aria-hidden="true" /></span>
                              <form className="organization-files-table__new-folder-form" onSubmit={submitFolder}>
                                <input
                                  id="organization-file-folder-name"
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={newFolderName}
                                  maxLength="100"
                                  autoFocus
                                  onChange={(event) => setNewFolderName(event.target.value)}
                                  placeholder="Mappenavn"
                                  aria-label="Mappenavn"
                                />
                                <button type="submit" className="btn btn-sm btn-primary organization-files-table__inline-action" disabled={creatingFolder || !newFolderName.trim()} aria-label="Opret mappe" title="Opret mappe">
                                  <i className="bi bi-check-lg" aria-hidden="true" />
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary organization-files-table__inline-action" onClick={cancelCreatingFolder} aria-label="Annuller" title="Annuller">
                                  <i className="bi bi-x-lg" aria-hidden="true" />
                                </button>
                              </form>
                            </div>
                          </td>
                          <td className="text-nowrap"><span className="workspace-chip">Mappe</span></td>
                          <td className="text-muted text-nowrap">—</td>
                          <td className="text-muted text-nowrap">—</td>
                          <td className="text-muted text-nowrap">—</td>
                          <td className="text-muted text-nowrap">—</td>
                          <td className="text-end text-muted">—</td>
                        </tr>
                      ) : null}

                      {visibleFolders.map((folder) => {
                        const dropKey = `row:${folder.id}`;

                        return (
                          <tr
                            key={`folder-${folder.id}`}
                            className={`organization-files-table__folder-row${dragTargetId === dropKey ? " is-drop-target" : ""}`}
                            draggable={folder.can_update && editingFolderId !== folder.id}
                            onDragStart={(event) => beginDrag(event, { type: "folder", id: folder.id, parentId: folder.parent_id })}
                            onDragEnd={clearDragState}
                            onDragOver={(event) => handleDragOver(event, folder.id, dropKey)}
                            onDragLeave={handleDragLeave}
                            onDrop={(event) => handleDrop(event, folder.id)}
                          >
                            <td>
                              <div className="organization-files-table__file">
                                <span className="organization-files-table__icon organization-files-table__icon--folder"><i className="bi bi-folder-fill" aria-hidden="true" /></span>
                                {editingFolderId === folder.id ? renderFolderRename(folder) : (
                                  <button type="button" className="organization-files-table__folder-link" onClick={() => navigateToFolder(folder.id)}>{folder.name}</button>
                                )}
                              </div>
                            </td>
                            <td className="text-nowrap"><span className="workspace-chip">Mappe</span></td>
                            <td className="text-muted text-nowrap">—</td>
                            <td className="text-muted text-nowrap">—</td>
                            <td className="text-muted text-nowrap">—</td>
                            <td className="text-muted text-nowrap">—</td>
                            <td className="text-end">
                              {(folder.can_update && editingFolderId !== folder.id) || folder.can_destroy ? (
                                <RowActionMenu label={`Handlinger for ${folder.name}`}>
                                  {folder.can_update && editingFolderId !== folder.id ? (
                                    <li>
                                      <button type="button" className="dropdown-item" onClick={() => startRenamingFolder(folder)}>
                                        <i className="bi bi-pencil" aria-hidden="true" />
                                        <span>Omdøb</span>
                                      </button>
                                    </li>
                                  ) : null}
                                  {folder.can_destroy ? (
                                    <li>
                                      <button type="button" className="dropdown-item text-danger" onClick={() => destroyFolder(folder)}>
                                        <i className="bi bi-trash" aria-hidden="true" />
                                        <span>Slet mappe</span>
                                      </button>
                                    </li>
                                  ) : null}
                                </RowActionMenu>
                              ) : <span className="text-muted">—</span>}
                            </td>
                          </tr>
                        );
                      })}

                      {sortedVisibleFiles.map((file) => (
                        <tr
                          key={`file-${file.id}`}
                          draggable={file.can_update && editingFileId !== file.id}
                          onDragStart={(event) => beginDrag(event, { type: "file", id: file.id, folderId: file.folder_id })}
                          onDragEnd={clearDragState}
                          onClick={(event) => toggleFileSelectionFromRow(event, file)}
                          onKeyDown={(event) => handleFileRowKeyDown(event, file)}
                          tabIndex={file.can_update && editingFileId !== file.id ? 0 : undefined}
                          aria-selected={selectedFileIdSet.has(file.id)}
                          className={`${file.can_update ? "organization-files-table__draggable-row" : ""}${selectedFileIdSet.has(file.id) ? " is-selected" : ""}`}
                        >
                          <td>
                            <div className="organization-files-table__file">
                              <span className="organization-files-table__icon"><i className="bi bi-file-earmark" aria-hidden="true" /></span>
                              {editingFileId === file.id ? (
                                <form className="organization-files-table__rename" onSubmit={(event) => submitFileRename(event, file)}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={editingFilename}
                                    maxLength="255"
                                    autoFocus
                                    onChange={(event) => {
                                      setEditingFilename(event.target.value);
                                      setRenameError("");
                                    }}
                                  />
                                  <button type="submit" className="btn btn-sm btn-primary">Gem</button>
                                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelRenaming}>Annuller</button>
                                  {renameError ? <div className="organization-files-table__rename-error">{renameError}</div> : null}
                                </form>
                              ) : <span className="organization-files-table__name" title={file.virtual_path}>{file.filename}</span>}
                            </div>
                          </td>
                          <td className="text-nowrap"><span className="workspace-chip">{fileTypeLabel(file)}</span></td>
                          <td className="text-muted text-nowrap">{formatBytes(file.byte_size)}</td>
                          <td>
                            <div className="organization-files-table__upload-meta">
                              <div className="workspace-admin-name organization-files-table__uploader" title={file.uploader.name}>{file.uploader.name}</div>
                              <small>{formatDate(file.created_at)}</small>
                            </div>
                          </td>
                          <td>
                            {file.last_edited_at ? (
                              <div className="organization-files-table__upload-meta">
                                <div className="workspace-admin-name organization-files-table__uploader" title={file.last_edited_by?.name}>
                                  {file.last_edited_by?.name || "Ukendt bruger"}
                                </div>
                                <small>{formatDate(file.last_edited_at)}</small>
                              </div>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-nowrap"><OpenaiStatusIcon file={file} /></td>
                          <td className="text-end">
                            <RowActionMenu label={`Handlinger for ${file.filename}`}>
                              {file.can_edit_in_onlyoffice ? (
                                <li>
                                  <a href={edit_organization_file_path(file.id)} className="dropdown-item">
                                    <i className={`bi ${onlyofficeEditIcon(file)}`} aria-hidden="true" />
                                    <span>Rediger</span>
                                  </a>
                                </li>
                              ) : null}
                              {file.content_type === "application/pdf" ? (
                                <li>
                                  <a
                                    href={organization_file_path(file.id, { preview: true })}
                                    className="dropdown-item"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <i className="bi bi-eye" aria-hidden="true" />
                                    <span>Vis</span>
                                  </a>
                                </li>
                              ) : null}
                              <li>
                                <a href={organization_file_path(file.id)} className="dropdown-item">
                                  <i className="bi bi-download" aria-hidden="true" />
                                  <span>Download</span>
                                </a>
                              </li>
                              {file.can_update && editingFileId !== file.id ? (
                                <li>
                                  <button type="button" className="dropdown-item" onClick={() => startRenamingFile(file)}>
                                    <i className="bi bi-pencil" aria-hidden="true" />
                                    <span>Omdøb</span>
                                  </button>
                                </li>
                              ) : null}
                              {file.can_update && (!file.openai_synced_at || file.openai_sync_error) ? (
                                <li>
                                  <button type="button" className="dropdown-item" onClick={() => retryOpenaiSync(file)}>
                                    <i className="bi bi-arrow-repeat" aria-hidden="true" />
                                    <span>Prøv AI-synkronisering igen</span>
                                  </button>
                                </li>
                              ) : null}
                              {file.can_destroy ? (
                                <li>
                                  <button type="button" className="dropdown-item text-danger" onClick={() => destroyFile(file)}>
                                    <i className="bi bi-trash" aria-hidden="true" />
                                    <span>Fjern</span>
                                  </button>
                                </li>
                              ) : null}
                            </RowActionMenu>
                          </td>
                        </tr>
                      ))}

                      {visibleFolders.length === 0 && sortedVisibleFiles.length === 0 ? (
                        <tr><td colSpan="7" className="text-muted py-4">Denne mappe er tom. Opret en undermappe eller upload filer.</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      </section>

      <MoveFilesDialog
        isOpen={moveDialogOpen}
        count={selectedFileIds.length}
        folders={folders}
        currentFolderId={currentFolderId}
        targetFolderId={moveTargetFolderId}
        moving={movingFiles}
        onTargetChange={setMoveTargetFolderId}
        onCancel={() => setMoveDialogOpen(false)}
        onMove={submitMoveDialog}
      />
    </>
  );
}

OrganizationFilesIndex.layout = (page) => (
  <AuthLayout header={{ breadcrumbs: [{ label: "Filer" }], title: "Filer" }}>
    {page}
  </AuthLayout>
);

export default OrganizationFilesIndex;
